import { html } from '@crossiety/lit-components';
import BaseMenu from '@crossiety/lit-components/components/base_menu/element.js';
import '@crossiety/lit-components/components/dot_menu/element.js';

import translations from './translations.js';

/**
 * Dot menu for card comment
 *
 * @prop ownerId - Id of the user who posted the comment
 * @prop cardId - Id of the card that owns the comment
 * @prop commentId - Id of the comment that owns the menu
 *
 * @slot trigger - For trigger button
 */

export default class CardCommentMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    ownerId: { type: Number },
    cardId: { type: String },
    commentId: { type: String },
  };

  connectedCallback() {
    super.connectedCallback();
    this.card = document.querySelector(`xs-card[uid="${this.cardId}"`);
  }

  /** @internal */
  get instanceId() {
    return this.commentId;
  }

  /** @internal */
  get baseUrl() {
    return `/cards/${this.cardId}/comments/${this.commentId}`;
  }

  /** @internal */
  get editUrl() {
    return `${this.baseUrl}/edit`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.commentId}&complaint[complainable_type]=CardComment`;
  }

  /** @internal */
  get currentUserId() {
    return this.AppConfig.user?.id;
  }

  /** @internal */
  get mutateOn() {
    if (!this.card) return false;

    return [...this.card.dataset.adminIds.split(','), this.ownerId]
      .map(Number)
      .filter(Boolean)
      .includes(this.currentUserId);
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('actions.edit'),
      action: this.actionFor('commentExecuteJSFromResponse', [this.editUrl]),
      icon: 'pen',
      partName: 'edit',
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.inappropriate_report'),
      action: this.actionFor('commentExecuteJSFromResponse', [this.reportUrl]),
      icon: 'hexagon-exclamation',
      partName: 'report',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('commentDelete'),
      role: 'destructive',
      icon: 'close',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get items() {
    return [
      this.mutateOn && this.editItem,
      this.reportItem,
      this.mutateOn && this.divider,
      this.mutateOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.cardTitle,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-card-comment-menu',
      id: this.instanceId,
      method: method,
      args: args,
    };
  }

  async commentExecuteJSFromResponse(url) {
    await this.commonActions.execJSFromResponse(url);
  }

  async commentDelete() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) return false;

    try {
      await this.commonActions.delete(this.baseUrl);
      this.commonActions.displaySnackBarSuccess(
        this.t('snack.comment_delete_success')
      );
    } catch (e) {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <slot name="trigger" slot="trigger">
          <button class="xs-btn btn-naked-dark" aria-label="toggle button">
            <ef-icon name="ellipsis"></ef-icon>
          </button>
        </slot>
      </ef-dot-menu>
    `;
  }
}

CardCommentMenu.registerTagName('xs-card-comment-menu');
CardCommentMenu.registerCommonActions('xs-card-comment-menu');

customElements.get('xs-card-comment-menu') ||
  customElements.define('xs-card-comment-menu', CardCommentMenu);
